import React, { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Skeleton,
  Paper,
  Checkbox,
  Typography,
  Chip,
  Stack,
  useTheme,
} from '@mui/material';
import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';
import {
  TablePeoProps,
  TableHeadCell,
  TableRow as TableRowType,
  EnhancedTableHeadProps,
  FakeLoadTableProps,
} from '../types/table';

// Enhanced Table Head Component
function EnhancedTableHead({
  headCells,
  order,
  orderBy,
  onRequestSort,
  selectable = false,
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
}: EnhancedTableHeadProps): React.JSX.Element {
  const theme = useTheme();

  const createSortHandler = (property: string) => (event: React.MouseEvent) => {
    onRequestSort(event, property);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onSelectAll) {
      onSelectAll(event.target.checked);
    }
  };

  return (
    <TableHead>
      <TableRow>
        {selectable && (
          <TableCell padding="checkbox" sx={{ width: 50 }}>
            <Checkbox
              indeterminate={selectedCount > 0 && selectedCount < totalCount}
              checked={totalCount > 0 && selectedCount === totalCount}
              onChange={handleSelectAll}
              sx={{
                color: theme.palette.primary.main,
                '&.Mui-checked': {
                  color: theme.palette.primary.main,
                },
              }}
            />
          </TableCell>
        )}

        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align || (headCell.firstCell ? 'left' : 'right')}
            padding="normal"
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `2px solid ${theme.palette.divider}`,
              width: headCell.width,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                cursor: headCell.sortable !== false ? 'pointer' : 'default',
              },
              transition: 'background-color 0.2s ease',
            }}
          >
            {headCell.sortable !== false ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                IconComponent={
                  orderBy === headCell.id
                    ? order === 'asc'
                      ? KeyboardArrowUpIcon
                      : KeyboardArrowDownIcon
                    : SortIcon
                }
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    fontSize: '1.2rem',
                  },
                  '&.Mui-active': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  },
                }}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              <Typography variant="subtitle2" component="span">
                {headCell.label}
              </Typography>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Loading Skeleton Component
function FakeLoadTable({
  tableRowCount,
  headCells,
  size = 'medium',
}: FakeLoadTableProps): React.JSX.Element {
  return (
    <TableBody>
      {Array.from(new Array(tableRowCount)).map((_, index) => (
        <TableRow
          key={index}
          sx={{ '&:hover': { backgroundColor: 'transparent' } }}
        >
          {headCells.map((headCell) => (
            <TableCell
              align={headCell.align || (headCell.firstCell ? 'left' : 'right')}
              padding="normal"
              key={headCell.id}
              sx={{ width: headCell.width }}
            >
              <Skeleton
                variant="text"
                width={headCell.width || '100%'}
                height={size === 'small' ? 20 : 24}
                animation="wave"
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

// Empty State Component
function EmptyState({
  message,
  headCells,
}: {
  message: string;
  headCells: TableHeadCell[];
}): React.JSX.Element {
  return (
    <TableBody>
      <TableRow>
        <TableCell colSpan={headCells.length} align="center" sx={{ py: 8 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h6" color="text.secondary">
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No data available to display
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

// Main Table Component
export default function TablePeo({
  data = [],
  isFetching = false,
  headCells,
  handleRowClick,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  pagination = true,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  size = 'medium',
  elevation = 2,
  striped = true,
  hover = true,
  emptyMessage = 'No results found',
  loadingRows = 5,
  maxHeight,
  actions,
  toolbar,
}: TablePeoProps): React.JSX.Element {
  const theme = useTheme();

  const handleRequestSort = (
    event: React.MouseEvent,
    property: string
  ): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowSelection = (
    rowId: string | number,
    checked: boolean
  ): void => {
    if (onSelectionChange) {
      const newSelection = checked
        ? [...selectedRows, rowId]
        : selectedRows.filter((id) => id !== rowId);
      onSelectionChange(newSelection);
    }
  };

  const isRowSelected = (rowId: string | number): boolean => {
    return selectedRows.includes(rowId);
  };

  const getCellValue = (
    row: TableRowType,
    headCell: TableHeadCell
  ): React.ReactNode => {
    if (!row.hasOwnProperty(headCell.id)) {
      return '';
    }

    let value = row[headCell.id];

    // Handle boolean values
    if (headCell.boolean && row.hasOwnProperty(headCell.boolean)) {
      const booleanValue = row[headCell.boolean];
      if (booleanValue === true) {
        value = row[headCell.id] === true ? 'Yes' : 'No';
      } else {
        value = row[headCell.id];
      }
    }

    // Handle different value types
    if (typeof value === 'boolean') {
      return (
        <Chip
          label={value ? 'Yes' : 'No'}
          size="small"
          color={value ? 'success' : 'default'}
          variant="outlined"
        />
      );
    }

    if (value === null || value === undefined) {
      return (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          -
        </Typography>
      );
    }

    if (headCell.format) {
      return headCell.format(value);
    }

    return value;
  };

  const totalCount = useMemo(() => data?.length || 0, [data]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Toolbar */}
      {toolbar && (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          {toolbar}
        </Paper>
      )}

      {/* Main Table */}
      <Paper elevation={elevation} sx={{ width: '100%', mb: 2 }}>
        <TableContainer sx={{ maxHeight }}>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={size}
          >
            <EnhancedTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              selectable={selectable}
              selectedCount={selectedRows.length}
              totalCount={totalCount}
              onSelectAll={(checked) => {
                if (onSelectionChange) {
                  onSelectionChange(checked ? data.map((row) => row.id) : []);
                }
              }}
            />

            {isFetching && (
              <FakeLoadTable
                tableRowCount={loadingRows}
                headCells={headCells}
                size={size}
              />
            )}

            {!isFetching && data.length === 0 && (
              <EmptyState message={emptyMessage} headCells={headCells} />
            )}

            {!isFetching && data.length > 0 && (
              <TableBody>
                {data.map((row, index) => {
                  const isSelected = isRowSelected(row.id);

                  return (
                    <TableRow
                      hover={hover}
                      onClick={(event) => handleRowClick?.(event, row.id)}
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id}
                      selected={isSelected}
                      sx={{
                        cursor: handleRowClick ? 'pointer' : 'default',
                        backgroundColor:
                          striped && index % 2 === 1
                            ? 'rgba(131, 1, 62, 0.1)'
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          transform: hover ? 'translateY(-1px)' : 'none',
                          boxShadow: hover ? theme.shadows[2] : 'none',
                        },
                        transition: 'all 0.2s ease',
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.light + '20',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.light + '30',
                          },
                        },
                      }}
                    >
                      {selectable && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={(event) =>
                              handleRowSelection(row.id, event.target.checked)
                            }
                            onClick={(event) => event.stopPropagation()}
                            sx={{
                              color: theme.palette.primary.main,
                              '&.Mui-checked': {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        </TableCell>
                      )}

                      {headCells.map((headCell) => (
                        <TableCell
                          key={headCell.id + index}
                          align={
                            headCell.align ||
                            (headCell.firstCell ? 'left' : 'right')
                          }
                          padding="normal"
                          sx={{
                            width: headCell.width,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          {getCellValue(row, headCell)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={totalCount < rowsPerPage ? totalCount : -1}
            rowsPerPage={totalCount < rowsPerPage ? totalCount : rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            slotProps={{
              actions: {
                nextButton: { disabled: totalCount < rowsPerPage },
              },
            }}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                {
                  fontSize: '0.875rem',
                },
            }}
          />
        )}
      </Paper>

      {/* Actions */}
      {actions && (
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
}
